import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useIsFetching, useIsMutating, useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";

import { updateSettings, type ValidatedPlatformSettings } from "../api";
import {
  PLATFORM_SETTINGS_MUTATION_KEY, PLATFORM_SETTINGS_QUERY_KEY,
  reconcilePlatformSettings, retryPlatformSettingsRead, SettingsAuthorityError,
} from "../settingsAuthority";
import { useSettingsAuthority, type SettingsDesiredMutation, type SettingsWriteLease } from "../settingsAuthorityStore";
import { settingsUpdateErrorMessage } from "../settingsErrors";

export function InquiryMessageSettingsPanel({ settings }: { settings: ValidatedPlatformSettings }) {
  const queryClient = useQueryClient();
  const authority = useSettingsAuthority(queryClient);
  useIsFetching({ exact: true, queryKey: PLATFORM_SETTINGS_QUERY_KEY });
  const writeCount = useIsMutating({ exact: true, mutationKey: PLATFORM_SETTINGS_MUTATION_KEY });
  const initialSequence = useRef(authority.resolution?.sequence ?? 0);
  const observedLimit = useRef(settings.initial_inquiry_message_limit);
  const [draft, setDraft] = useState(String(settings.initial_inquiry_message_limit));
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const recovery = authority.recovery?.kind === "inquiry-limit" ? authority.recovery : null;
  const resolution = authority.resolution && authority.resolution.sequence > initialSequence.current
    && authority.resolution.desired.kind === "inquiry-limit" ? authority.resolution : null;
  const readUncertain = queryClient.getQueryState(PLATFORM_SETTINGS_QUERY_KEY)?.status === "error";
  const blocked = readUncertain || authority.recovery !== null || authority.refreshing || authority.writing || writeCount > 0;
  const parsed = draft.trim() === "" ? Number.NaN : Number(draft);
  const valid = Number.isInteger(parsed) && parsed >= 1 && parsed <= 10;
  const dirty = valid && parsed !== settings.initial_inquiry_message_limit;

  useEffect(() => {
    if (observedLimit.current === settings.initial_inquiry_message_limit) return;
    observedLimit.current = settings.initial_inquiry_message_limit;
    setDraft(String(settings.initial_inquiry_message_limit));
    setErrorMessage(null);
  }, [settings.initial_inquiry_message_limit]);

  const mutation = useMutation({
    mutationKey: PLATFORM_SETTINGS_MUTATION_KEY,
    mutationFn: async ({ lease, limit }: { lease: SettingsWriteLease; limit: number }) => {
      await updateSettings({ initial_inquiry_message_limit: limit });
      if (!authority.isWriteOperationCurrent(lease)) return null;
      return reconcilePlatformSettings(queryClient, () => authority.isWriteOperationCurrent(lease));
    },
  });

  async function save() {
    if (!valid || !dirty || blocked || mutation.isPending
      || queryClient.isMutating({ exact: true, mutationKey: PLATFORM_SETTINGS_MUTATION_KEY }) > 0
      || !authority.tryStartWrite()) return;
    const lease = authority.beginWriteOperation();
    if (!lease) { authority.finishWrite(); return; }
    const desired = Object.freeze({ kind: "inquiry-limit", limit: parsed }) satisfies SettingsDesiredMutation;
    setErrorMessage(null);
    try {
      const confirmed = await mutation.mutateAsync({ lease, limit: parsed });
      if (!confirmed || !authority.isWriteOperationCurrent(lease)) return;
      setDraft(String(confirmed.initial_inquiry_message_limit));
      authority.recordResolution(desired, confirmed.initial_inquiry_message_limit === desired.limit ? "confirmed" : "diverged");
    } catch (error) {
      if (!authority.isWriteOperationCurrent(lease)) return;
      if (error instanceof SettingsAuthorityError) authority.recordRecovery(desired);
      else setErrorMessage(settingsUpdateErrorMessage(error, "Failed to update the message limit."));
    } finally {
      authority.finishWrite(lease);
    }
  }

  async function retry() {
    if (!recovery || mutation.isPending || !authority.tryStartRefresh(recovery)) return;
    const pending = recovery;
    try {
      const confirmed = await retryPlatformSettingsRead(queryClient);
      setDraft(String(confirmed.initial_inquiry_message_limit));
      authority.resolveRecovery(pending, confirmed.initial_inquiry_message_limit === pending.limit ? "confirmed" : "diverged");
    } catch {
      authority.finishRefresh(pending);
    }
  }

  return (
    <Paper component="section" variant="outlined" sx={{ p: 2.5 }}>
      <Stack spacing={2}>
        <Typography component="h2" variant="h6" sx={{ fontWeight: 700 }}>Inquiry messages</Typography>
        <Typography variant="body2" color="text.secondary">
          Limits initial messages across a player’s pending inquiries at one facility.
          Staff replies unlock that conversation. Changing this limit keeps existing message counts.
        </Typography>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ alignItems: { xs: "stretch", sm: "flex-start" } }}>
          <TextField label="Initial messages per facility" type="number" value={draft}
            disabled={readUncertain} error={draft.length > 0 && !valid}
            onChange={(event) => { setDraft(event.target.value); setErrorMessage(null); }}
            slotProps={{ htmlInput: { min: 1, max: 10, step: 1 } }} sx={{ maxWidth: 320 }} />
          <Button size="small" variant="contained" disabled={!valid || !dirty || blocked || mutation.isPending}
            onClick={() => void save()} sx={{ mt: { sm: 1 } }}>
            {mutation.isPending ? "Saving…" : "Save message limit"}
          </Button>
        </Stack>
        {!valid ? <Typography color="error" variant="body2">Enter a whole number from 1 to 10.</Typography> : null}
        {recovery ? (
          <Alert severity="error" action={<Button color="inherit" size="small" disabled={authority.refreshing}
            onClick={() => void retry()}>{authority.refreshing ? "Retrying…" : "Retry"}</Button>}>
            The saved message limit could not be confirmed. Retry to refresh settings.
          </Alert>
        ) : resolution ? (
          <Alert severity={resolution.outcome === "confirmed" ? "success" : "error"}>
            {resolution.outcome === "confirmed" ? "Message limit updated."
              : "The latest message limit differs from the requested value. Review it before trying again."}
          </Alert>
        ) : errorMessage ? <Alert severity="error">{errorMessage}</Alert> : null}
      </Stack>
    </Paper>
  );
}
